import { trpc } from '@/lib/trpc';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Wish } from '../wish';

export type UpsertResponse = {
	status: number;
	image_key: string | null;
	insertedWishId?: number;
};

const useUpsertWish = () => {
	const { t } = useTranslation();
	const utils = trpc.useUtils();
	const navigate = useNavigate();
	const { id_wish } = useParams();

	const wishFormSchema = z.object({
		id: z.number().optional(),
		url: z.union([z.string().length(0), z.string().url()]).optional(),
		name: z
			.string()
			.min(1, { message: t('common.error.required') })
			.max(255),
		price: z
			.string()
			.transform((inputPrice) =>
				inputPrice.trim() !== ''
					? parseFloat(inputPrice.replace(',', '.'))
					: 0
			)
			.refine((value) => !isNaN(value) && value >= 0, {
				message: t('wish.form.error.price'),
			})
			.nullable(),
		comment: z.string().max(1024).optional(),
		image: z.any().optional(),
		image_url: z.union([z.string().length(0), z.string().url()]).optional(),
		scrapped: z.boolean(),
	});

	type UpsertWishParams = {
		wish: z.infer<typeof wishFormSchema>;
		currentWish?: Wish;
		id_list: number;
		image: File | undefined;
	};

	const { mutate, isPending } = useMutation({
		mutationFn: async ({
			wish,
			id_list,
			image,
			currentWish,
		}: UpsertWishParams) => {
			const formData = new FormData();
			formData.append('name', wish.name);
			formData.append('comment', wish.comment || '');
			formData.append('price', wish.price?.toString() || '');
			image && formData.append('image', image as Blob);
			formData.append('url', wish.url || '');
			formData.append('fk_list', id_list.toString());
			formData.append('image_url', wish.image_url || '');
			formData.append('image_key', currentWish?.image_key || '');
			formData.append('status', 'active');
			if (currentWish?.id)
				formData.append('id', currentWish.id.toString());

			const upsert = await fetch(`${import.meta.env.VITE_API_URL}/wish`, {
				method: 'POST',
				body: formData,
				credentials: 'include',
			});
			const response: UpsertResponse = await upsert.json();
			if (response.status !== 200 && response.status !== 201) {
				throw response;
			}
			return response;
		},
		onMutate: async ({ wish, id_list, currentWish, image }) => {
			const successToast = toast.success(
				currentWish
					? t('wish.form.notification.success.edit')
					: t('wish.form.notification.success.create')
			);
			await utils.wish.get.cancel();

			const previousWishes = utils.wish.get.getData({
				id_list: id_list,
			}) || {
				status: 200,
				wishes: [],
			};

			const randomId = Math.random() * 100;

			const fakeWish: Wish = {
				id: currentWish?.id || randomId,
				created_at: currentWish?.created_at || new Date().toISOString(),
				updated_at: new Date().toISOString(),
				comment: wish.comment || null,
				fk_list: id_list,
				image_key:
					wish.url || image ? currentWish?.image_key || null : null,
				name: wish.name,
				price: wish.price,
				url: wish.url || null,
				fk_user_reserved: currentWish?.fk_user_reserved || null,
				status: 'active',
			};

			const optimisticWishes = getOptimisticWishes(
				previousWishes.wishes,
				fakeWish,
				randomId
			);

			utils.wish.get.setData(
				{ id_list: id_list },
				{ status: 200, wishes: optimisticWishes }
			);

			return { previousWishes, randomId, successToast };
		},
		onError(_, { id_list }, context) {
			if (!context?.previousWishes) return;
			utils.wish.get.setData(
				{ id_list: id_list },
				{ status: 200, wishes: context?.previousWishes.wishes }
			);
			toast.dismiss(context.successToast);
			toast.error(t('error.generic'));
		},
		onSuccess: (response, { id_list, currentWish }, { randomId }) => {
			utils.wish.get.setData(
				{ id_list: id_list },
				{
					status: 200,
					wishes:
						utils.wish.get
							.getData({ id_list: id_list })
							?.wishes.map((wish) => {
								if (
									wish.id === randomId ||
									wish.id === currentWish?.id
								) {
									return {
										...wish,
										id: response.insertedWishId || wish.id,
										image_key: response.image_key,
									};
								}
								return wish;
							}) || [],
				}
			);
			if (id_wish && response.insertedWishId) {
				navigate(`/list/${id_list}/${response.insertedWishId}`);
			}
		},
	});

	return {
		upsertWish: mutate,
		upsertWishLoading: isPending,
		wishFormSchema,
	};
};

const getOptimisticWishes = (
	wishes: Wish[],
	optimisticWish: Wish,
	randomId: number
) => {
	if (!wishes) return [optimisticWish];
	if (optimisticWish.id && randomId !== optimisticWish.id)
		return wishes.map((wish) =>
			wish.id === optimisticWish.id ? optimisticWish : wish
		);
	return [...wishes, optimisticWish];
};

export default useUpsertWish;
